// Product Card Component - With Copy Feedback
import { useState } from 'react';
import { CuratedProduct } from '../types';

interface ProductCardProps {
  product: CuratedProduct;
  platformConfig: {
    gradient: string;
    bg: string;
  };
  onCopyLink: (link: string) => void;
}

export default function ProductCard({ product, platformConfig, onCopyLink }: ProductCardProps) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success'>('idle');

  const handleCopy = async () => {
    try {
      // Use Clipboard API with fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(product.affiliate_link);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = product.affiliate_link;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('idle'), 2000);
      
      if (onCopyLink) {
        onCopyLink(product.affiliate_link);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      setCopyStatus('idle');
    }
  };

  return (
    <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700 hover:border-amber-500 transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/10">
      {/* Rank Badge */}
      <div className={`bg-gradient-to-r ${platformConfig.gradient} px-3 py-2`}>
        <span className="font-bold text-white text-sm md:text-base">
          #{product.rank_number}
        </span>
      </div>

      {/* Product Info */}
      <div className="p-3 md:p-4">
        <img
          src={product.image_url}
          alt={product.title}
          className="w-full h-28 md:h-36 lg:h-48 object-cover rounded-lg mb-3 bg-slate-700"
        />
        
        <h3 className="text-xs md:text-sm font-semibold text-slate-100 line-clamp-2 mb-2 min-h-[2rem] md:min-h-[3rem]">
          {product.title}
        </h3>

        {/* Badges */}
        <div className="flex flex-wrap gap-1 mb-2">
          {product.badges.map((badge, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 md:px-2 md:py-1 bg-slate-700/50 rounded text-[10px] md:text-xs text-amber-400 border border-slate-600"
            >
              {badge}
            </span>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-1.5 md:gap-2 text-xs mb-2">
          <div className="bg-slate-700/50 rounded px-1.5 py-1 text-center">
            <span className="text-emerald-400 font-bold">{product.commission_rate}%</span>
            <br />
            <span className="text-slate-500 text-[10px] md:text-xs">Komisi</span>
          </div>
          <div className="bg-slate-700/50 rounded px-1.5 py-1 text-center">
            <span className="text-yellow-400 font-bold text-xs">⭐ {product.rating}</span>
            <br />
            <span className="text-slate-500 text-[10px] md:text-xs">{product.review_count.toLocaleString()}</span>
          </div>
        </div>

        {/* Price & Sales */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm md:text-base lg:text-xl font-bold text-emerald-400">
            Rp{product.price.toLocaleString('id-ID')}
          </span>
          <span className="text-slate-500 text-[10px] md:text-xs">
            {product.sales_count.toLocaleString()} sold
          </span>
        </div>

        {/* Copy Button with Visual Feedback */}
        <button
          onClick={handleCopy}
          className={`w-full py-2 md:py-3 text-xs md:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1 md:gap-2 ${
            copyStatus === 'success'
              ? 'bg-emerald-500 text-white'
              : `${platformConfig.bg} text-white hover:opacity-90`
          }`}
        >
          <span>{copyStatus === 'success' ? '✅' : '📋'}</span>
          <span>{copyStatus === 'success' ? 'Berhasil Disalin!' : 'Salin Tautan'}</span>
        </button>
      </div>
    </div>
  );
}
